import { useEffect, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import {
  CORNER_ORDER, type Corner, PREDICTED_COLORS, RECORDED_COLORS, SERIES_INDEX,
} from "./overlayConstants";
import type { OverlayData } from "./overlayAdapter";
import {
  ZOOM_GROUP, broadcastReset, broadcastZoom, isBroadcasting,
  joinZoomGroup, runSilently, type XRange, type ZoomMember,
} from "./zoomSync";
import { exportChartPng } from "./chartExport";

// onCursorIdx lifts the crosshair index to the parent (the Results pane) so the
// F-V operating-point dots can ride the same cursor. It fires on sync-driven
// moves too, so hovering any chart in the "damper-overlay" group updates it.
export type OverlayChartProps = {
  data: OverlayData;
  title?: string;
  onCursorIdx?: (idx: number | null) => void;
};

const INITIAL_HEIGHT = 320;

export function OverlayChart({ data, title, onCursorIdx }: OverlayChartProps) {
  const hostRef   = useRef<HTMLDivElement>(null);
  const chartRef  = useRef<uPlot | null>(null);
  const latestRef = useRef(data);
  const memberRef = useRef<ZoomMember | null>(null);
  const onCursorIdxRef = useRef(onCursorIdx);
  const [visible, setVisible] = useState<Set<Corner>>(() => new Set(CORNER_ORDER));
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);

  useEffect(() => { latestRef.current = data; }, [data]);
  useEffect(() => { onCursorIdxRef.current = onCursorIdx; }, [onCursorIdx]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const width = Math.max(320, Math.floor(rect.width || 640));
    const reportCursor = (idx: number | null): void => {
      setCursorIdx(idx);
      onCursorIdxRef.current?.(idx);
    };
    const opts = buildOptions(latestRef, reportCursor, memberRef, {
      width, height: INITIAL_HEIGHT,
    });
    // Construct silently so the initial setScale does not broadcast a zoom.
    let u!: uPlot;
    runSilently(() => {
      u = new uPlot(opts, data.data as uPlot.AlignedData, host);
    });
    chartRef.current = u;

    // Join the shared zoom group: full range comes from the latest x row.
    const member: ZoomMember = {
      applyX: (range: XRange | null) => {
        const cur = chartRef.current;
        if (!cur) return;
        const xs = latestRef.current.data[0] as number[];
        const target = range ?? { min: xs[0], max: xs[xs.length - 1] };
        runSilently(() => cur.setScale("x", target));
      },
    };
    memberRef.current = member;
    const leaveGroup = joinZoomGroup(ZOOM_GROUP, member);

    const onMouseLeave = (): void => reportCursor(null);
    u.over.addEventListener("mouseleave", onMouseLeave);

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const cur = chartRef.current;
        if (!cur) continue;
        cur.setSize({
          width: Math.max(320, Math.floor(e.contentRect.width)),
          height: INITIAL_HEIGHT,
        });
      }
    });
    ro.observe(host);

    return () => {
      u.over.removeEventListener("mouseleave", onMouseLeave);
      ro.disconnect();
      leaveGroup();
      memberRef.current = null;
      u.destroy();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const u = chartRef.current;
    if (!u) return;
    const xs = data.data[0] as number[];
    u.batch(() => {
      u.setData(data.data as uPlot.AlignedData, false);
      // Silent: a data refresh re-pins x to the full window and must not read
      // as a user zoom to the rest of the group.
      runSilently(() => u.setScale("x", { min: xs[0], max: xs[xs.length - 1] }));
    });
  }, [data]);

  useEffect(() => {
    const u = chartRef.current;
    if (!u) return;
    for (const c of CORNER_ORDER) {
      const show = visible.has(c);
      u.setSeries(SERIES_INDEX.predicted(c), { show });
      u.setSeries(SERIES_INDEX.recorded(c),  { show: show && data.hasRecorded[c] });
    }
  }, [visible, data.hasRecorded]);

  const toggle = (c: Corner): void =>
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const pngName = `${(title ?? "damper-overlay").replace(/[^a-z0-9]+/gi, "_")}.png`;

  return (
    <div data-testid="overlay-chart" className="overlay-chart">
      <div className="overlay-chrome">
        {title ? <div className="overlay-title">{title}</div> : <span />}
        <button
          type="button"
          className="chart-export-btn"
          data-testid="overlay-export-png"
          title="Export chart as PNG"
          aria-label="Export chart as PNG"
          onClick={() => exportChartPng(chartRef.current, pngName)}
        >
          PNG
        </button>
      </div>
      <div ref={hostRef} className="overlay-canvas" />
      <CursorReadout data={data} cursorIdx={cursorIdx} visible={visible} />
      <div role="group" aria-label="corner toggles" className="overlay-legend">
        {CORNER_ORDER.map((c) => {
          const active = visible.has(c);
          return (
            <button
              key={c}
              type="button"
              data-corner={c}
              data-active={active}
              onClick={() => toggle(c)}
              className="overlay-legend-chip"
              data-testid={`overlay-chip-${c}`}
              style={{ ["--chip-color" as never]: PREDICTED_COLORS[c] }}
            >
              <span className="chip-swatch" />
              <span className="chip-label">{c.toUpperCase()}</span>
              {!data.hasRecorded[c] && (
                <span className="chip-rec-absent" title="recorded trace absent for this corner">
                  no rec
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CursorReadout({
  data, cursorIdx, visible,
}: {
  data: OverlayData;
  cursorIdx: number | null;
  visible: Set<Corner>;
}) {
  const formatValue = (v: number | null | undefined): string =>
    v === null || v === undefined || !Number.isFinite(v) ? "—" : `${v.toFixed(1)} mm`;
  return (
    <div className="overlay-readout" data-testid="overlay-readout"
         role="status" aria-label="cursor readout">
      {CORNER_ORDER.map((c) => {
        const active = visible.has(c);
        const predIdx = SERIES_INDEX.predicted(c);
        const recIdx  = SERIES_INDEX.recorded(c);
        const predVal = cursorIdx === null ? null : (data.data[predIdx][cursorIdx] as number);
        const recVal  = cursorIdx === null ? null : (data.data[recIdx][cursorIdx] as number | null);
        const recText = !data.hasRecorded[c] ? "no rec" : formatValue(recVal);
        return (
          <div
            key={c}
            className="overlay-readout-row"
            data-corner={c}
            data-active={active}
            data-testid={`overlay-readout-${c}`}
          >
            <span className="readout-swatch" style={{ background: PREDICTED_COLORS[c] }} />
            <span className="readout-label">{c.toUpperCase()}</span>
            <span className="readout-pred">pred {formatValue(predVal)}</span>
            <span className="readout-rec">rec {recText}</span>
          </div>
        );
      })}
    </div>
  );
}

function buildOptions(
  latest: React.MutableRefObject<OverlayData>,
  reportCursor: (idx: number | null) => void,
  memberRef: React.MutableRefObject<ZoomMember | null>,
  size: { width: number; height: number },
): uPlot.Options {
  return {
    ...size,
    scales: {
      x: { time: false },
      y: {
        range: (): [number, number] => latest.current.yDomain,
      },
    },
    cursor: {
      drag: { setScale: true, x: true, y: false },
      y: false,
      sync: { key: ZOOM_GROUP },
      bind: {
        // Double-click resets EVERY synced chart to its full range, not just
        // this one.
        dblclick: (_self: uPlot) => (_e: MouseEvent) => {
          broadcastReset(ZOOM_GROUP);
          return null;
        },
      },
    },
    hooks: {
      setCursor: [
        (self: uPlot) => {
          const idx = self.cursor.idx;
          reportCursor(typeof idx === "number" ? idx : null);
        },
      ],
      // Drag-zoom changes the x-scale; mirror it to the rest of the group.
      // The guard skips programmatic + broadcast-driven changes.
      setScale: [
        (self: uPlot, key: string) => {
          if (key !== "x" || isBroadcasting()) return;
          const member = memberRef.current;
          if (!member) return;
          const s = self.scales.x;
          if (s?.min == null || s?.max == null) return;
          broadcastZoom(ZOOM_GROUP, member, { min: s.min, max: s.max });
        },
      ],
    },
    series: [
      {},
      ...CORNER_ORDER.map((c) => ({
        label: `${c.toUpperCase()} rec`,
        stroke: RECORDED_COLORS[c],
        width: 1,
      })),
      ...CORNER_ORDER.map((c) => ({
        label: c.toUpperCase(),
        stroke: PREDICTED_COLORS[c],
        width: 1.5,
      })),
    ],
    axes: [
      { stroke: "#9a9a9a", grid: { stroke: "#2a2a2a", dash: [2, 2] } },
      { stroke: "#9a9a9a", grid: { stroke: "#2a2a2a", dash: [2, 2] }, label: "damper-pos (mm)" },
    ],
    legend: { show: false },
  };
}
